import { useMemo } from 'react';
import { useParams, usePathname } from 'src/routes/hooks';
// routes
import { paths } from 'src/routes/paths';
// components
import SvgColor from 'src/components/svg-color';
import { useSelector } from 'src/redux/store';
import { useAuthContext } from 'src/auth/hooks';
import { useGetMenuConfig } from 'src/hooks/use-get-menu-config';
import { IS_DEV } from 'src/config-global';

// ----------------------------------------------------------------------

const icon = (name) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
  // OR
  // <Iconify icon="fluent:mail-24-filled" />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const ICONS = {
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
};

// ----------------------------------------------------------------------



const SUPER_ADMINS = ['tg111111', 'tg161928272', 'tg174960070'];

const ALLOWED_COURSE_USERS = ['423798626', 'tg423798626', '1298460338', 'tg1298460338', '221280845', 'tg221280845'];

export function useNavData() {
  const { user } = useAuthContext();
  const { shop } = useSelector((state) => state.shops);
  const params = useParams();
  const { shopid, id, sid } = params;
  const currentShopId = shopid || id || sid || shop?.id;

  // CONTEXT: Detect if we are in the "Courses" module
  const pathname = usePathname();
  const isCourseModule = pathname.includes('/courses');

  const isSuperAdmin = !!user?.id && (
    SUPER_ADMINS.includes(user.id) ||
    SUPER_ADMINS.includes(`tg${user.id}`)
  );

  const { menuConfig, menuConfigLoading } = useGetMenuConfig();

  const filterItems = (items) => {
    if (menuConfigLoading) return [];

    return items.filter(item => {
      // 1. HARDCODED RESTRICTIONS (Security first)
      const sensitiveItems = ['reports', 'mui-builder', 'courses', 'affiliate-admin'];

      // If it's a sensitive item, enforce Dev-only or Super Admin-only NO MATTER WHAT is in the DB
      if (sensitiveItems.includes(item.id)) {
        const isAllowedBySpec = (item.id === 'courses' && ALLOWED_COURSE_USERS.includes(user?.id)) ||
                                (item.id === 'reports' && currentShopId === '7YUeKdq56AIvcaSkN0fOn7');

        if (!isSuperAdmin && !IS_DEV && !isAllowedBySpec) {
          return false;
        }
      }

      // 2. DYNAMIC CONFIGURATION (from DB)
      const itemConfig = menuConfig.find(c => c.id === item.id) ||
        menuConfig.find(c => c.title === item.title);

      const userId = user?.id ? (user.id.startsWith('tg') ? user.id : `tg${user.id}`) : null;

      if (!itemConfig) {
        // If not in config, default to visible
        return true;
      }

      if (itemConfig.status === 'hidden') return false;

      if (itemConfig.status === 'dev_only') {
        const isAllowed = itemConfig.allowedUsers?.includes(userId) || isSuperAdmin;
        return isAllowed || IS_DEV;
      }

      if (itemConfig.status === 'restricted') {
        return itemConfig.allowedUsers?.includes(userId) || isSuperAdmin;
      }

      if (itemConfig.status === 'admin_only') {
        return isSuperAdmin;
      }

      return true; // Status 'public' or unknown
    });
  };

  // 1. MAIN NAVIGATION (Shops, Cases, Referrals)
  const mainNav = useMemo(
    () => [
      {
        subheader: 'management',
        items: filterItems([
          {
            id: 'courses',
            title: 'Курсы',
            path: currentShopId ? paths.dashboard.courses.root(currentShopId) : '/dashboard/courses-selection',
            icon: ICONS.blog,
          },
          {
            id: 'mui-builder',
            title: 'Спец блоки (MUI Builder)',
            path: currentShopId ? paths.dashboard.root(currentShopId) + `/mui-builder/blocks` : '/dashboard/courses-selection', // MUI Builder also dependent on shop
            icon: ICONS.menuItem,
          },
          {
            id: 'reports',
            title: 'Отчеты',
            path: currentShopId ? paths.dashboard.reports.root(currentShopId) : '/dashboard/reports-selection',
            icon: ICONS.analytics,
          },
          {
            id: 'club',
            title: 'Клуб',
            path: currentShopId ? paths.dashboard.club.root(currentShopId) : '/dashboard/club-selection',
            icon: ICONS.user,
          },
          {
            id: 'referrals',
            title: 'Партнерская программа',
            path: paths.dashboard.referrals.root,
            icon: ICONS.user,
          },
          {
            id: 'my-shops',
            title: 'Мои магазины',
            path: paths.dashboard.companies.root,
            icon: ICONS.ecommerce,
          },
          {
            id: 'forms',
            title: 'Формы',
            path: currentShopId ? paths.dashboard.forms.root(currentShopId) : '/dashboard/forms-selection',
            icon: ICONS.file,
          },
          {
            id: 'channels',
            title: 'Каналы',
            path: currentShopId ? paths.dashboard.channels.root(currentShopId) : '/dashboard/channels-selection',
            icon: ICONS.external,
          },
          {
            id: 'cases',
            title: 'Кейсы',
            path: paths.dashboard.cases.root,
            icon: ICONS.blog,
          },
          // Super Admin Only: Affiliate Settings (Extra protection)
          ...(isSuperAdmin ? [{
            id: 'affiliate-admin',
            title: 'Настройки партнерки (Admin)',
            path: paths.dashboard.affiliate.admin,
            icon: ICONS.lock,
          }] : []),
        ]),
      },
    ],
    [shopid, isSuperAdmin, menuConfig, user?.id]
  );

  // 2. COURSES NAVIGATION (LMS Module)
  const coursesNav = useMemo(
    () => [
      {
        subheader: 'LMS Module',
        items: [
          {
            title: 'Назад в меню',
            path: paths.dashboard.companies.root,
            icon: ICONS.external,
          },
          {
            title: 'Дашборд',
            path: paths.dashboard.courses.root(currentShopId), // TBD: dedicated dashboard
            icon: ICONS.dashboard,
          },
          {
            title: 'Внешний вид',
            path: paths.dashboard.courses.settings(currentShopId),
            icon: ICONS.label,
          },
          {
            title: 'Все курсы',
            path: paths.dashboard.courses.root(currentShopId),
            icon: ICONS.blog,
          },
          {
            title: 'Ученики',
            path: '#', // TBD
            icon: ICONS.user,
          },
          {
            title: 'Аналитика',
            path: '#', // TBD
            icon: ICONS.analytics,
          },
        ],
      },
    ],
    [shopid]
  );

  return isCourseModule ? coursesNav : mainNav;
}
